import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import {
  getKidData,
  getLatestKidOverview,
  getKidOverviewHistoricalData,
  getKidCostVariables,
  getKidCompareData,
  getKidMarkerAll,
  getKidMarker,
  getJurisdictions,
  getLanguages,
  getLatestKidFigures,
  KidCostVariables,
  getAllKidLayoutStyles,
  KidLayoutStyles,
  readKidLayoutTemplate,
  ReadKidLayoutTemplateParams,
  getAllKidTemplates,
  getNecessaryKidTemplates,
  getKidTemplateIncompleteDocuments,
  getKidTemplate,
  getKidTemplateAffectedShareClasses,
  getKidTemplateSetupStatus,
} from '../kids.routes';
import { AxiosError } from 'axios';
import {
  KidOverviewDataItem,
  KidMarker,
  KidFiguresRecordModel,
  KidLayoutTemplateModel,
  KidTemplateAffectedShareClassesResponse,
  KidTemplateIncompleteDocumentsResponse,
  NecessaryKidTemplatesResponse,
  KidTemplateOutputModel,
  KidTemplateSetupStatusResponse,
} from '../types';

export const useGetKidData = (
  clientId: string,
  fundId: string,
  shareClass: string,
  documentLanguage: string,
  version?: string,
) => {
  return useQuery({
    queryKey: [
      'kid-data',
      clientId,
      fundId,
      shareClass,
      documentLanguage,
      version,
    ],
    queryFn: () =>
      getKidData(clientId, fundId, shareClass, documentLanguage, version),
    enabled: !!clientId && !!fundId && !!shareClass && !!documentLanguage,
  });
};

/** Latest overview row(s) for a client; with fund/share class/language, a one-item array for that document. */
export function useGetLatestKidOverview(
  clientId: string,
  fundId?: string,
  shareClassId?: string,
  documentLanguage?: string,
  options?: Omit<
    UseQueryOptions<KidOverviewDataItem[], AxiosError<any>>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<KidOverviewDataItem[], AxiosError<any>>({
    queryKey: [
      'kid-latest-overview',
      clientId,
      fundId,
      shareClassId,
      documentLanguage,
    ],
    queryFn: () =>
      getLatestKidOverview(clientId, fundId, shareClassId, documentLanguage),
    enabled: !!clientId,
    ...options,
  });
}

// Hook for fetching historical KID overview data
export function useGetKidOverviewHistoricalData(
  clientId?: string,
  fundId?: string,
  shareClassId?: string,
  documentLanguage?: string,
  options?: Omit<
    UseQueryOptions<Record<string, KidOverviewDataItem[]>, AxiosError<any>>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<Record<string, KidOverviewDataItem[]>, AxiosError<any>>({
    queryKey: [
      'kid-historical-overview',
      clientId,
      fundId,
      shareClassId,
      documentLanguage,
    ],
    queryFn: () =>
      getKidOverviewHistoricalData(
        clientId!,
        fundId,
        shareClassId,
        documentLanguage,
      ),
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
    enabled: !!clientId,
    ...options,
  });
}

export function useGetKidCostVariables(
  clientId?: string,
  fundId?: string,
  shareClassId?: string,
  options?: Omit<
    UseQueryOptions<KidCostVariables, AxiosError<any>>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<KidCostVariables, AxiosError<any>>({
    queryKey: ['kid-cost-variables', clientId, fundId, shareClassId],
    queryFn: () => getKidCostVariables(clientId!, fundId!, shareClassId!),
    enabled: !!clientId && !!fundId && !!shareClassId,
    ...options,
  });
}

// Hook for fetching KID comparison data
export function useGetKidCompareData(
  clientId?: string,
  fundId?: string,
  shareClassId?: string,
  comparisonVersion?: string,
  targetVersion?: string,
  documentLanguage?: string,
  options?: Omit<
    UseQueryOptions<
      { comparison_version: any[]; target_version: any[] },
      AxiosError<any>
    >,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<
    { comparison_version: any[]; target_version: any[] },
    AxiosError<any>
  >({
    queryKey: [
      'kid-compare-data',
      clientId,
      fundId,
      shareClassId,
      comparisonVersion,
      targetVersion,
      documentLanguage,
    ],
    queryFn: () =>
      getKidCompareData(
        clientId!,
        fundId!,
        shareClassId!,
        comparisonVersion!,
        targetVersion!,
        documentLanguage || 'English',
      ),
    enabled:
      !!clientId &&
      !!fundId &&
      !!shareClassId &&
      !!comparisonVersion &&
      !!targetVersion,
    ...options,
  });
}

// Hook for fetching all KID markers
export function useGetKidMarkerAll(
  clientId?: string,
  fundId?: string,
  shareClassId?: string,
  kidFigureCalculationDate?: string,
  overviewLastUpdatingDatetime?: string,
  withValue?: boolean,
  options?: Omit<
    UseQueryOptions<KidMarker[], AxiosError<any>>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<KidMarker[], AxiosError<any>>({
    queryKey: [
      'kid-marker-all',
      clientId,
      fundId,
      shareClassId,
      kidFigureCalculationDate,
      overviewLastUpdatingDatetime,
      withValue,
    ],
    queryFn: () =>
      getKidMarkerAll(
        clientId!,
        fundId!,
        shareClassId!,
        kidFigureCalculationDate,
        overviewLastUpdatingDatetime,
        withValue,
      ),
    enabled: !!clientId && !!fundId && !!shareClassId,
    ...options,
  });
}

export function useGetKidMarker(
  clientId?: string,
  fundId?: string,
  shareClassId?: string,
  markerType?: string,
  kidFigureCalculationDate?: string,
  overviewLastUpdatingDatetime?: string,
  withValue: boolean = false,
  options?: Omit<
    UseQueryOptions<KidMarker[], AxiosError<any>>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<KidMarker[], AxiosError<any>>({
    queryKey: [
      'kid-marker',
      clientId,
      fundId,
      shareClassId,
      markerType,
      kidFigureCalculationDate,
      overviewLastUpdatingDatetime,
      withValue,
    ],
    queryFn: () =>
      getKidMarker(
        clientId!,
        fundId!,
        shareClassId!,
        markerType!,
        kidFigureCalculationDate,
        overviewLastUpdatingDatetime,
        withValue,
      ),
    enabled: !!clientId && !!fundId && !!shareClassId && !!markerType,
    ...options,
  });
}

// Hook for fetching all jurisdictions
export function useKidJurisdictions(
  options?: Omit<
    UseQueryOptions<string[], AxiosError<any>>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<string[], AxiosError<any>>({
    queryKey: ['kid-jurisdictions'],
    queryFn: () => getJurisdictions(),
    ...options,
  });
}

// Hook for fetching all languages
export function useKidLanguages(
  options?: Omit<
    UseQueryOptions<string[], AxiosError<any>>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<string[], AxiosError<any>>({
    queryKey: ['kid-languages'],
    queryFn: () => getLanguages(),
    ...options,
  });
}

export function useGetLatestKidFigures(
  clientId?: string,
  fundId?: string,
  shareClassId?: string,
  options?: Omit<
    UseQueryOptions<KidFiguresRecordModel[] | null, AxiosError<any>>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<KidFiguresRecordModel[] | null, AxiosError<any>>({
    queryKey: ['kid-latest-figures', clientId, fundId, shareClassId],
    queryFn: () => getLatestKidFigures(clientId!, fundId!, shareClassId),
    enabled: !!clientId && !!fundId,
    ...options,
  });
}

export function useGetAllKidLayoutStyles(
  clientId: string,
  options?: Omit<
    UseQueryOptions<KidLayoutStyles, AxiosError<any>>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<KidLayoutStyles, AxiosError<any>>({
    queryKey: ['kid-layout-styles', clientId],
    queryFn: () => getAllKidLayoutStyles(clientId),
    enabled: !!clientId,
    ...options,
  });
}

export function useGetKidLayoutTemplate(
  params?: ReadKidLayoutTemplateParams,
  options?: Omit<
    UseQueryOptions<KidLayoutTemplateModel[], AxiosError<any>>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<KidLayoutTemplateModel[], AxiosError<any>>({
    queryKey: [
      'kid-layout-template',
      params?.kidLayoutId,
      params?.kidLayoutLastUpdatingTimestamp,
      params?.fundId,
      params?.shareClassId,
      params?.kidFigureCalculationDate,
    ],
    queryFn: () => readKidLayoutTemplate(params!),
    enabled:
      options?.enabled !== undefined
        ? options.enabled
        : !!params?.kidLayoutId && !!params?.kidLayoutLastUpdatingTimestamp,
    ...options,
  });
}

export function useGetAllKidTemplates(
  clientId?: string,
  templateType?: string,
  onlyShowActiveTemplates: boolean = true,
  options?: Omit<
    UseQueryOptions<KidTemplateOutputModel[] | null, AxiosError<any>>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<KidTemplateOutputModel[] | null, AxiosError<any>>({
    queryKey: [
      'kid-templates',
      clientId,
      templateType,
      onlyShowActiveTemplates,
    ],
    queryFn: () =>
      getAllKidTemplates(clientId!, templateType, onlyShowActiveTemplates),
    enabled: !!clientId,
    ...options,
  });
}

export function useGetNecessaryKidTemplates(
  clientId?: string,
  fundId?: string,
  documentLanguage?: string,
  options?: Omit<
    UseQueryOptions<NecessaryKidTemplatesResponse, AxiosError<any>>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<NecessaryKidTemplatesResponse, AxiosError<any>>({
    queryKey: ['kid-necessary-templates', clientId, fundId, documentLanguage],
    queryFn: () =>
      getNecessaryKidTemplates(clientId!, fundId, documentLanguage),
    enabled: !!clientId,
    ...options,
  });
}

export function useGetKidTemplateIncompleteDocuments(
  clientId?: string,
  fundId?: string,
  shareClassId?: string,
  options?: Omit<
    UseQueryOptions<KidTemplateIncompleteDocumentsResponse, AxiosError<any>>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<KidTemplateIncompleteDocumentsResponse, AxiosError<any>>({
    queryKey: ['kid-template-incomplete-documents', clientId, fundId, shareClassId],
    queryFn: () =>
      getKidTemplateIncompleteDocuments(clientId!, fundId, shareClassId),
    enabled: !!clientId,
    ...options,
  });
}

export function useGetKidTemplate(
  clientId?: string,
  templateId?: string,
  params?: {
    templateLastUpdatingDatetime?: string;
    fundId?: string;
    shareClassId?: string;
    kidFigureCalculationDate?: string;
  },
  options?: Omit<
    UseQueryOptions<KidTemplateOutputModel | null, AxiosError<any>>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<KidTemplateOutputModel | null, AxiosError<any>>({
    queryKey: ['kid-template', clientId, templateId, params],
    queryFn: () => getKidTemplate(clientId!, templateId!, params),
    enabled: !!clientId && !!templateId,
    ...options,
  });
}

export function useGetKidTemplateAffectedShareClasses(
  clientId?: string,
  templateId?: string,
  fundId?: string,
  options?: Omit<
    UseQueryOptions<KidTemplateAffectedShareClassesResponse, AxiosError<any>>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<KidTemplateAffectedShareClassesResponse, AxiosError<any>>({
    queryKey: [
      'kid-template-affected-share-classes',
      clientId,
      templateId,
      fundId,
    ],
    queryFn: () =>
      getKidTemplateAffectedShareClasses(clientId!, templateId!, fundId),
    enabled: !!clientId && !!templateId,
    ...options,
  });
}

export function useGetKidTemplateSetupStatus(
  clientId?: string,
  templateId?: string,
  fundId?: string,
  options?: Omit<
    UseQueryOptions<KidTemplateSetupStatusResponse, AxiosError<any>>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<KidTemplateSetupStatusResponse, AxiosError<any>>({
    queryKey: ['kid-template-setup-status', clientId, templateId, fundId],
    queryFn: () => getKidTemplateSetupStatus(clientId!, templateId!, fundId),
    enabled: !!clientId && !!templateId,
    ...options,
  });
}
